import ShopSection from './components/ShopSection'
import { useState } from 'react'
import './styles/index.css'
import NewsSection from './NewsSection'

function App() {
  const [activeTab, setActiveTab] = useState('home')
  const [searchQuery, setSearchQuery] = useState('')

  const navItems: Array<{id: string; label: string}> = []

  // Resource categories
  const categories = [
    { id: 'health', name: 'Health Benefits', icon: '💚', count: 12 },
    { id: 'technique', name: 'Technique Guide', icon: '🥋', count: 8 },
    { id: 'philosophy', name: 'Philosophy', icon: '☯️', count: 6 },
    { id: 'history', name: 'History', icon: '📜', count: 5 },
    { id: 'community', name: 'Community', icon: '🌍', count: 9 },
    { id: 'events', name: 'Events', icon: '📅', count: 15 },
    { id: 'videos', name: 'Video Tutorials', icon: '🎥', count: 20 },
    { id: 'research', name: 'Research Papers', icon: '🔬', count: 10 },
  ]

  // Featured resources
  const featuredResources = [
    {
      id: 1,
      title: 'Beginners Guide to Tai Chi',
      description: 'Comprehensive guide covering basic movements, breathing techniques, and practice tips for beginners.',
      url: '#',
      category: 'technique',
      rating: 4.9,
      tags: ['beginner', 'guide', 'movements']
    },
    {
      id: 2,
      title: 'Scientific Health Benefits',
      description: 'Collection of peer-reviewed studies demonstrating the health benefits of regular Tai Chi practice.',
      url: '#',
      category: 'health',
      rating: 4.8,
      tags: ['research', 'health', 'science']
    },
    {
      id: 3,
      title: 'Tai Chi Philosophy Course',
      description: 'Explore the philosophical foundations of Tai Chi and how Yin-Yang balance applies to daily life.',
      url: '#',
      category: 'philosophy',
      rating: 4.7,
      tags: ['philosophy', 'yin-yang', 'wisdom']
    },
    {
      id: 4,
      title: 'History of Tai Chi Documentary',
      description: 'Tracing the evolution of Tai Chi from its ancient origins to modern practice worldwide.',
      url: '#',
      category: 'history',
      rating: 4.6,
      tags: ['history', 'documentary', 'origins']
    },
    {
      id: 5,
      title: 'Global Community Directory',
      description: 'Find Tai Chi schools, teachers, and practice groups in your local area and worldwide.',
      url: '#',
      category: 'community',
      rating: 4.5,
      tags: ['directory', 'schools', 'teachers']
    },
    {
      id: 6,
      title: 'Daily Practice App',
      description: 'Mobile application with guided Tai Chi sessions, progress tracking, and community features.',
      url: '#',
      category: 'videos',
      rating: 4.4,
      tags: ['app', 'practice', 'daily']
    },
  ]

  // Events
  const events = [
    { name: 'Global Tai Chi Day', date: 'April 26, 2026', type: 'Hybrid', location: 'Worldwide', attendees: '10M+' },
    { name: 'Central Park Gathering', date: 'April 26, 2026', type: 'In-Person', location: 'New York, USA', attendees: '5,000+' },
    { name: 'London Festival', date: 'April 25, 2026', type: 'In-Person', location: 'London, UK', attendees: '3,000+' },
    { name: 'Beginners Workshop', date: 'April 24, 2026', type: 'Online', location: 'Virtual', attendees: 'Unlimited' },
    { name: 'MasterClass Series', date: 'May 2026', type: 'Online', location: 'Virtual', attendees: '1,000+' },
    { name: 'Wudang Summit', date: 'June 2026', type: 'In-Person', location: 'Wudang, China', attendees: '2,000+' },
  ]

  // Filter resources based on search
  const filteredResources = featuredResources.filter(resource =>
    resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    resource.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    resource.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  )

  const currentYear = new Date().getFullYear()

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-green-500 flex items-center justify-center">
                <span className="text-white text-xl font-bold">☯</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">International Tai Chi Day</h1>
                <p className="text-gray-600 text-sm">Celebrating the Art of Taijiquan</p>
              </div>
            </div>

            <nav className="hidden md:flex items-center space-x-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`px-4 py-2 rounded-lg transition-colors duration-200 ${
                    activeTab === item.id
                      ? 'bg-blue-50 text-blue-600 font-medium'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            <div className="relative">
              <input
                type="text"
                placeholder="Search resources..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-48 lg:w-64"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <span className="absolute left-3 top-3 text-gray-400">🔍</span>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-indigo-50 to-green-50">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-4xl">
            <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <span className="text-blue-500">☯️</span>
              <span className="text-sm font-medium">International Tai Chi Day</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Celebrate the Art of
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-green-500"> Tai Chi</span>
            </h1>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-600 mb-4">
              International Tai Chi Day
            </h2>

            <p className="text-xl text-gray-700 mb-10 max-w-3xl">
              Join millions worldwide in celebrating the ancient art of Tai Chi and Qigong.
              A global movement promoting health, harmony, and peace through traditional Chinese practices.
            </p>

            <div className="flex flex-wrap gap-4 mb-12">
              <button className="px-8 py-3.5 bg-gradient-to-r from-blue-600 to-green-500 text-white font-semibold text-lg rounded-lg hover:from-blue-700 hover:to-green-600 hover:shadow-lg transition-all duration-200 shadow-md">
                Find Events Near You
              </button>
              <button className="px-8 py-3.5 bg-white text-gray-800 font-semibold text-lg rounded-lg border border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200">
                Get Involved
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 text-center shadow-sm">
                <div className="text-3xl font-bold text-blue-600 mb-1">100+</div>
                <div className="text-sm text-gray-600">Countries</div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 text-center shadow-sm">
                <div className="text-3xl font-bold text-green-500 mb-1">10M+</div>
                <div className="text-sm text-gray-600">Participants</div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 text-center shadow-sm">
                <div className="text-3xl font-bold text-blue-600 mb-1">5,000+</div>
                <div className="text-sm text-gray-600">Events</div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 text-center shadow-sm">
                <div className="text-3xl font-bold text-green-500 mb-1">25+</div>
                <div className="text-sm text-gray-600">Years</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ShopSection />

      {/* News Section */}
      <NewsSection
        website="taijiquanday"
        limit={10000}
        title="Tai Chi News & Updates"
        themeColor="purple"
      />

      {/* Features Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Celebrate Tai Chi Day?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              International Tai Chi Day brings together millions of people worldwide to practice
              and promote the ancient art of Tai Chi for health, harmony, and peace.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-6 border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-200">
              <div className="text-4xl mb-3">📅</div>
              <h3 className="font-semibold text-gray-900 mb-2">Global Events</h3>
              <p className="text-gray-600 text-sm">Find Tai Chi Day events happening worldwide, from local gatherings to virtual sessions.</p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-200">
              <div className="text-4xl mb-3">📚</div>
              <h3 className="font-semibold text-gray-900 mb-2">Learning Resources</h3>
              <p className="text-gray-600 text-sm">Access free tutorials, articles, and videos for all skill levels and experience.</p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-200">
              <div className="text-4xl mb-3">👥</div>
              <h3 className="font-semibold text-gray-900 mb-2">Community</h3>
              <p className="text-gray-600 text-sm">Connect with Tai Chi practitioners and organizations around the globe.</p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-200">
              <div className="text-4xl mb-3">💚</div>
              <h3 className="font-semibold text-gray-900 mb-2">Health Benefits</h3>
              <p className="text-gray-600 text-sm">Discover the physical and mental health benefits of regular Tai Chi practice.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Browse Resources by Category</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Explore our curated collection of Tai Chi resources
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categories.map((category) => (
              <div
                key={category.id}
                className="bg-white rounded-xl p-6 border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-200 cursor-pointer"
              >
                <div className="text-3xl mb-3">{category.icon}</div>
                <h3 className="font-semibold text-gray-900 mb-1">{category.name}</h3>
                <p className="text-sm text-gray-500">{category.count} resources</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Resources */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Featured Resources</h2>
              <p className="text-gray-600">Handpicked Tai Chi learning materials and guides</p>
            </div>
            <button className="px-6 py-2.5 text-blue-600 font-medium rounded-lg border border-blue-200 hover:bg-blue-50 transition-colors duration-200">
              View All Resources
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredResources.map((resource) => (
              <div key={resource.id} className="bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <span className="inline-block px-3 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full mb-2">
                        {resource.category}
                      </span>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{resource.title}</h3>
                    </div>
                    <div className="flex items-center bg-gray-100 rounded-lg px-2 py-1">
                      <span className="text-yellow-500 mr-1">⭐</span>
                      <span className="font-medium">{resource.rating}</span>
                    </div>
                  </div>

                  <p className="text-gray-600 mb-4">{resource.description}</p>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {resource.tags.map((tag, index) => (
                      <span key={index} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <a
                    href={resource.url}
                    className="inline-flex items-center text-blue-600 font-medium hover:text-blue-700 transition-colors duration-200"
                  >
                    Learn More
                    <span className="ml-2">→</span>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Upcoming Events</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Join Tai Chi practitioners around the world for these upcoming events
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${
                      event.type === 'Online' ? 'bg-green-100 text-green-800' :
                      event.type === 'In-Person' ? 'bg-blue-100 text-blue-800' :
                      'bg-purple-100 text-purple-800'
                    }`}>
                      {event.type}
                    </span>
                    <span className="text-sm text-gray-500">{event.attendees}</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{event.name}</h3>
                  <div className="text-sm text-gray-600 space-y-1">
                    <div className="flex items-center">
                      <span className="mr-2">📅</span>
                      <span>{event.date}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="mr-2">📍</span>
                      <span>{event.location}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Voices from the Community</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Hear from Tai Chi practitioners, teachers, and health professionals about their experiences
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-8 border border-gray-100 shadow-lg">
              <div className="text-4xl mb-4">💬</div>
              <p className="text-gray-600 italic mb-6 leading-relaxed">
                "As a physician, I've seen firsthand the transformative power of Tai Chi. My patients who practice regularly show remarkable improvements in balance, flexibility, and stress management."
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-green-400 rounded-full flex items-center justify-center text-white font-bold text-lg">M</div>
                <div className="ml-3">
                  <div className="font-semibold text-gray-900">Dr. Maria Rodriguez</div>
                  <div className="text-sm text-gray-500">Medical Doctor, Spain</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 border border-gray-100 shadow-lg">
              <div className="text-4xl mb-4">💬</div>
              <p className="text-gray-600 italic mb-6 leading-relaxed">
                "After retiring, I struggled with joint pain and lack of purpose. Discovering Tai Chi through International Tai Chi Day changed my life. I feel more balanced and peaceful than ever."
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-400 rounded-full flex items-center justify-center text-white font-bold text-lg">J</div>
                <div className="ml-3">
                  <div className="font-semibold text-gray-900">James Wilson</div>
                  <div className="text-sm text-gray-500">Retired Engineer, USA</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 border border-gray-100 shadow-lg">
              <div className="text-4xl mb-4">💬</div>
              <p className="text-gray-600 italic mb-6 leading-relaxed">
                "International Tai Chi Day brings together practitioners from all styles and backgrounds, creating a beautiful global harmony that reflects the essence of Tai Chi itself."
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-emerald-400 rounded-full flex items-center justify-center text-white font-bold text-lg">L</div>
                <div className="ml-3">
                  <div className="font-semibold text-gray-900">Li Wei</div>
                  <div className="text-sm text-gray-500">Tai Chi Master, China</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-blue-600 via-indigo-700 to-blue-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Join the Global Movement
          </h2>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Whether you're a seasoned practitioner or new to Tai Chi, there's a place for you
            in the International Tai Chi Day celebration.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <button className="px-8 py-4 bg-white text-blue-700 font-semibold text-lg rounded-lg hover:bg-gray-100 transition-colors shadow-lg">
              Get Involved Today
            </button>
            <button className="px-8 py-4 bg-transparent border-2 border-white text-white font-semibold text-lg rounded-lg hover:bg-white hover:text-blue-700 transition-colors">
              Find Events
            </button>
          </div>
          <div className="inline-block bg-white/10 backdrop-blur-sm rounded-xl px-8 py-6">
            <p className="text-xl font-semibold text-gray-100 mb-3">Contact us:</p>
            <a
              href="mailto:info@taijiquanday.com"
              className="inline-flex items-center bg-white text-blue-700 px-6 py-3 rounded-lg text-lg font-bold hover:bg-gray-100 transition-colors shadow-lg"
            >
              📧 info@taijiquanday.com
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-green-400 flex items-center justify-center">
                  <span className="text-white text-lg font-bold">☯</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold">International Tai Chi Day</h3>
                  <p className="text-gray-400 text-sm">taijiquanday.com</p>
                </div>
              </div>
              <p className="text-gray-400">
                Promoting health, harmony, and peace through Tai Chi and Qigong worldwide.
                Join the global celebration every April.
              </p>
              <div className="flex space-x-4 mt-6">
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:bg-blue-600 hover:text-white transition-all">𝕏</a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:bg-blue-600 hover:text-white transition-all">F</a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:bg-pink-600 hover:text-white transition-all">IG</a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:bg-red-600 hover:text-white transition-all">YT</a>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-6">Contact & Related Sites</h4>
              <div className="space-y-4 text-gray-400">
                <div className="flex items-center space-x-3">
                  <span>📧</span>
                  <a
                    href="mailto:info@taijiquanday.com"
                    className="hover:text-white transition-colors duration-200"
                  >
                    info@taijiquanday.com
                  </a>
                </div>
                <div className="flex items-center space-x-3">
                  <span>🌐</span>
                  <a
                    href="https://taijiquanday.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-white transition-colors duration-200"
                  >
                    taijiquanday.com
                  </a>
                </div>
                <div className="pt-4 space-y-2">
                  <h5 className="text-gray-300 font-semibold text-sm">Related Websites</h5>
                  <a href="https://hellowudang.com" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 hover:text-white transition-colors">
                    <span>🏔️</span>
                    <span>Wudang Mountain Official Portal</span>
                  </a>
                  <a href="https://wudanghub.com" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 hover:text-white transition-colors">
                    <span>🥋</span>
                    <span>Wudang Martial Arts Resources</span>
                  </a>
                </div>
                <p className="text-sm pt-4">
                  This is an independent project dedicated to promoting Tai Chi and Qigong worldwide.
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-500">
            <p>© {currentYear} International Tai Chi Day. All rights reserved.</p>
            <p className="text-sm mt-2">Built with React, TypeScript, and Tailwind CSS · Deployed on Cloudflare Pages</p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default App
